import { router, usePage } from '@inertiajs/react'
import { ChevronsUpDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { persistedUiParam } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { useAgentChatSidebar } from '@/frontend/pages/Dashboard/AgentChats/agentChatSidebarStore'
import { AssistantSessionList } from '@/frontend/features/AgentChats/assistant-ui/components/AssistantSessionList'
import ContextUsageBadge from '@/frontend/features/AgentChats/assistant-ui/components/ContextUsageBadge'
import type { SharedProps } from '@/frontend/types/inertia'

// Dashboard sidebar Chat mode (SZ-1504): an agent picker over the
// org's digital workers + the selected agent's grouped session list. The picker
// options come from the always-shared `navigableResources.workers`; the live
// session list comes from the mounted AgentChats page via the external store.
// Picking an agent navigates to that worker's chat (the page then republishes).

function workerInitial(name: string | null): string {
  return name?.trim().charAt(0).toUpperCase() || '?'
}

export function SidebarConversationsPanel({ onNavigate }: { onNavigate?: () => void }) {
  const page = usePage<SharedProps>()
  const workers = page.props.navigableResources?.workers ?? []
  const snapshot = useAgentChatSidebar()

  // Prefer the URL's worker as the immediate source of truth so the picker and
  // the list never disagree while the mounted chat page republishes the store.
  const urlWorkerId = new URLSearchParams(page.url.split('?')[1] ?? '').get('digital_worker_id')
  const activeWorkerId = urlWorkerId ?? snapshot?.activeWorkerId ?? null
  const activeWorker = workers.find((worker) => worker.id === activeWorkerId) ?? null
  const activeName = activeWorker?.name ?? snapshot?.workerName ?? null
  // Non-null only once the store has caught up to the selected worker.
  const readyWorkerId =
    snapshot && activeWorkerId && snapshot.activeWorkerId === activeWorkerId ? activeWorkerId : null

  function selectWorker(workerId: string) {
    if (workerId === activeWorkerId) return
    const params = new URLSearchParams({ digital_worker_id: workerId })
    const ui = persistedUiParam()
    if (ui) params.set('ui', ui)
    router.visit(`/dashboard/agent_chats?${params.toString()}`)
    onNavigate?.()
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2 px-2" data-testid="agent-chat-history-sidebar">
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="agent-chat-worker-picker"
              className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-card px-2 py-2 text-left transition-colors hover:bg-accent"
            >
            {/* Initial chips carry the agent's stable identity hue (B.4); the
                brand color is only the fallback while no agent is selected. */}
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                activeWorkerId ? 'bg-agent text-agent-foreground' : 'bg-primary text-primary-foreground',
              )}
              style={activeWorkerId ? agentHueStyle(activeWorkerId) : undefined}
            >
              {workerInitial(activeName)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-foreground">
                {activeName ?? 'Escolha um colaborador'}
              </span>
              {activeWorker?.job_title && (
                <span className="block truncate text-xs text-muted-foreground">{activeWorker.job_title}</span>
              )}
            </span>
              <ChevronsUpDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width] min-w-56">
          {workers.length === 0 ? (
            <DropdownMenuItem disabled>Nenhum agente disponível</DropdownMenuItem>
          ) : (
            workers.map((worker) => (
              <DropdownMenuItem
                key={worker.id}
                data-testid={`agent-chat-worker-option-${worker.id}`}
                onClick={() => selectWorker(worker.id)}
                aria-current={worker.id === activeWorkerId ? 'true' : undefined}
                className="gap-2"
              >
                <span
                  className="flex size-6 shrink-0 items-center justify-center rounded-full bg-agent text-[11px] font-medium text-agent-foreground"
                  style={agentHueStyle(worker.id)}
                >
                  {workerInitial(worker.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{worker.name}</span>
                  {worker.job_title && (
                    <span className="block truncate text-xs text-muted-foreground">{worker.job_title}</span>
                  )}
                </span>
                <span
                  className={cn(
                    'size-1.5 shrink-0 rounded-full bg-agent',
                    worker.id === activeWorkerId ? 'opacity-100' : 'opacity-0',
                  )}
                  style={agentHueStyle(worker.id)}
                />
              </DropdownMenuItem>
            ))
          )}
          </DropdownMenuContent>
        </DropdownMenu>
        {readyWorkerId && snapshot?.contextUsage && (
          <ContextUsageBadge
            usage={snapshot.contextUsage}
            side="right"
            align="start"
            className="size-6"
          />
        )}
      </div>

      {readyWorkerId && snapshot ? (
        <div className="min-h-0 flex-1">
          <AssistantSessionList
            activeSessionId={snapshot.activeSessionId}
            isNewConversation={snapshot.isNewConversation}
            sessions={snapshot.sessions}
            projects={snapshot.projects}
            workerId={readyWorkerId}
            newConversationDisabled={!snapshot.canStartNewConversation}
            onNewConversation={() => {
              snapshot.onNewConversation()
              onNavigate?.()
            }}
            onRenameSession={snapshot.onRenameSession}
            onPinSession={snapshot.onPinSession}
            onArchiveSession={snapshot.onArchiveSession}
            onDeleteSession={snapshot.onDeleteSession}
            onMoveSessionToProject={snapshot.onMoveSessionToProject}
            onCreateProject={snapshot.onCreateProject}
            onRenameProject={snapshot.onRenameProject}
            onDeleteProject={snapshot.onDeleteProject}
            onNavigate={onNavigate}
          />
        </div>
      ) : activeWorkerId ? (
        <p className="flex flex-1 items-center justify-center px-4 py-8 text-center text-sm text-muted-foreground">
          Carregando conversas…
        </p>
      ) : (
        <p className="flex flex-1 items-center justify-center px-4 py-8 text-center text-sm text-muted-foreground">
          Selecione um colaborador para ver as conversas.
        </p>
      )}
    </div>
  )
}
